import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function PayoutDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ account_holder: "", bank_name: "", account_number: "", ifsc_or_routing: "", tax_id: "", country: "" });
  const [status, setStatus] = useState("not_submitted");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/host/payout").then((r) => {
      const d = r.data;
      setStatus(d.kyc_status || "not_submitted");
      setForm({
        account_holder: d.account_holder || "",
        bank_name: d.bank_name || "",
        account_number: d.account_number_last4 ? `••••${d.account_number_last4}` : "",
        ifsc_or_routing: d.ifsc_or_routing || "",
        tax_id: d.tax_id || "",
        country: d.country || "",
      });
    });
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.put("/host/payout", { ...form, account_number: form.account_number.replace(/•/g, "") });
      toast.success("KYC submitted — under review");
      setStatus("pending");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const label = { not_submitted: "Not submitted", pending: "Under review", approved: "Approved", rejected: "Rejected — resubmit" }[status];
  const color = { pending: "text-terracotta", approved: "text-moss", rejected: "text-destructive" }[status] || "text-muted2";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-canvas max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Payout & KYC</DialogTitle>
        </DialogHeader>
        <div className="text-sm mb-2">Status: <span className={`font-medium ${color}`} data-testid="kyc-status">{label}</span></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Account holder name</Label>
            <Input data-testid="payout-holder" value={form.account_holder} onChange={(e) => setForm({...form, account_holder: e.target.value})} className="rounded-lg bg-white border-subtle" />
          </div>
          <div>
            <Label className="text-xs">Bank name</Label>
            <Input data-testid="payout-bank" value={form.bank_name} onChange={(e) => setForm({...form, bank_name: e.target.value})} className="rounded-lg bg-white border-subtle" />
          </div>
          <div>
            <Label className="text-xs">Country</Label>
            <Input data-testid="payout-country" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} className="rounded-lg bg-white border-subtle" />
          </div>
          <div>
            <Label className="text-xs">Account number</Label>
            <Input data-testid="payout-account" value={form.account_number} onChange={(e) => setForm({...form, account_number: e.target.value})} className="rounded-lg bg-white border-subtle" />
          </div>
          <div>
            <Label className="text-xs">IFSC / Routing / SWIFT</Label>
            <Input data-testid="payout-ifsc" value={form.ifsc_or_routing} onChange={(e) => setForm({...form, ifsc_or_routing: e.target.value})} className="rounded-lg bg-white border-subtle" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Tax ID (PAN / TIN)</Label>
            <Input data-testid="payout-tax" value={form.tax_id} onChange={(e) => setForm({...form, tax_id: e.target.value})} className="rounded-lg bg-white border-subtle" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="save-payout" onClick={submit} disabled={saving} className="rounded-full bg-moss hover:bg-moss-hover text-canvas">
            {saving ? "Saving…" : "Save & submit for review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
